import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import TopNavbarBack from "../../components/TopNavbarBack";
import Navbar from "../../components/Navbar";
import { toFarsiNumber } from "../../custom/helpers";
import { useGroupApi } from "./groupApi";
import { GroupAvatar, ConfirmSheet } from "./components/ui";
import GroupChat from "./components/GroupChat";
import GroupCalendar from "./components/GroupCalendar";
import GroupMembers from "./components/GroupMembers";
import InviteModal from "./components/InviteModal";
import GroupSettings from "./components/GroupSettings";
import CreateGroupSheet from "./components/CreateGroupSheet";
import "../../styles/Fonts.css";
import "../../styles/Buttons.css";
import "../../styles/Groups.css";

const GROUP_POLL_MS = 10000;

function useIsDesktop() {
  const q = "(min-width: 900px)";
  const [match, setMatch] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setMatch(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return match;
}

export default function GroupPage() {
  const { id } = useParams();
  const { call } = useGroupApi();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState(null);
  const [tab, setTab] = useState("chat");
  const [showInvite, setShowInvite] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [busy, setBusy] = useState(false);
  const goneRef = useRef(false);

  // Removed from the group / group deleted / never a member -> leave gracefully.
  const onGone = useCallback(() => {
    if (goneRef.current) return;
    goneRef.current = true;
    toast.info("این گروه دیگه در دسترس نیست");
    navigate("/groups", { replace: true });
  }, [navigate]);

  const loadGroup = useCallback(async () => {
    try {
      setGroup(await call(`groups/${id}`, { method: "GET" }));
    } catch (e) {
      if (e.status === 404) onGone();
    }
  }, [call, id, onGone]);

  const loadMembers = useCallback(async () => {
    try {
      const r = await call(`groups/${id}/members`, { method: "GET" });
      setMembers(r.members);
    } catch (e) {
      if (e.status === 404) onGone();
    }
  }, [call, id, onGone]);

  const refreshAll = useCallback(() => { loadGroup(); loadMembers(); }, [loadGroup, loadMembers]);

  useEffect(() => {
    goneRef.current = false;
    setGroup(null);
    setMembers(null);
    refreshAll();
    // revalidate periodically: catches permission changes, removal, renames
    const t = setInterval(() => !document.hidden && refreshAll(), GROUP_POLL_MS);
    return () => clearInterval(t);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const leave = async () => {
    setBusy(true);
    try {
      await call(`groups/${id}/leave`, { method: "POST" });
      toast.success("از گروه خارج شدی");
      goneRef.current = true;
      navigate("/groups", { replace: true });
    } catch (e) {
      toast.error(e.message);
      setBusy(false);
      setConfirmLeave(false);
    }
  };

  if (!group) {
    return (
      <div className="grpPage yekan">
        <TopNavbarBack title="" rbuttonIcon={["icon-iconchevron-right"]} rbackground={["rectangle1"]} lbuttonIcon={["dNone"]} lbuttonHref={["null"]} lbackground={["null"]} />
        <div className="grpWrap">
          <div className="grpSkeleton" style={{ height: 80, marginBottom: 12 }} />
          <div className="grpSkeleton" style={{ height: 320 }} />
        </div>
        <Navbar />
      </div>
    );
  }

  const me = group.me;
  const tabs = isDesktop
    ? [["overview", "تقویم و گفتگو"], ["members", "اعضا"]]
    : [["chat", "گفتگو"], ["calendar", "تقویم"], ["members", "اعضا"]];
  const activeTab = isDesktop ? (tab === "members" ? "members" : "overview") : tab === "overview" ? "chat" : tab;

  const chat = <GroupChat key={`chat-${id}`} groupId={id} onGone={onGone} />;
  const calendar = <GroupCalendar key={`cal-${id}`} groupId={id} canEdit={me.can_edit_calendar} onRefreshGroup={refreshAll} />;

  return (
    <div className="grpPage yekan">
      <TopNavbarBack title="" rbuttonIcon={["icon-iconchevron-right"]} rbackground={["rectangle1"]} lbuttonIcon={["icon-iconplus"]} lbackground={["rectangle1"]} lfunction={() => setShowCreate(true)} />
      <div className="grpWrap">
        <div className="grpHeader">
          <GroupAvatar name={group.name} image={group.image_path} size="xl" />
          <div className="grpHeaderInfo">
            <h1 className="grpTitle yekan">{group.name}</h1>
            <div className="grpSub yekan">{toFarsiNumber(group.member_count)} عضو</div>
            {group.description && <div className="grpSub yekan">{group.description}</div>}
          </div>
          <div className="grpHeaderActions">
            {me.is_owner && (
              <>
                <button type="button" className="grpIconBtn" aria-label="دعوت" onClick={() => setShowInvite(true)}>
                  <span className="icon-iconuser-add"></span>
                </button>
                <button type="button" className="grpIconBtn" aria-label="تنظیمات گروه" onClick={() => setShowSettings(true)}>
                  <span className="icon-iconsettings"></span>
                </button>
              </>
            )}
          </div>
        </div>

        <div className="grpTabs" role="tablist">
          {tabs.map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={activeTab === key} className={`grpTab yekan ${activeTab === key ? "grpTab-active" : ""}`} onClick={() => setTab(key)}>
              {label}
            </button>
          ))}
        </div>

        {activeTab === "overview" && (
          <div className="grpLayout grpLayout-split">
            {calendar}
            {chat}
          </div>
        )}
        {activeTab === "chat" && chat}
        {activeTab === "calendar" && calendar}
        {activeTab === "members" && (
          <>
            <GroupMembers groupId={id} me={me} members={members} onChanged={refreshAll} onInvite={() => setShowInvite(true)} />
            {!me.is_owner && (
              <button type="button" className="grpBtn grpBtn-danger grpBtn-block" style={{ marginTop: 14 }} onClick={() => setConfirmLeave(true)}>
                خروج از گروه
              </button>
            )}
          </>
        )}
      </div>

      {showCreate && <CreateGroupSheet onClose={() => setShowCreate(false)} />}
      {showInvite && <InviteModal group={group} onClose={() => setShowInvite(false)} />}
      {showSettings && (
        <GroupSettings
          group={group}
          members={members}
          onClose={() => setShowSettings(false)}
          onUpdated={(g) => { if (g) { setGroup(g); setShowSettings(false); } else refreshAll(); }}
          onMembersChanged={loadMembers}
          onDeleted={() => { goneRef.current = true; navigate("/groups", { replace: true }); }}
        />
      )}
      {confirmLeave && (
        <ConfirmSheet
          title="خروج از گروه"
          message="اگه از گروه خارج بشی، دیگه به گفتگو و تقویم مشترک دسترسی نداری. برای برگشتن باید دوباره دعوت بشی."
          confirmLabel="آره، خارج شو"
          busy={busy}
          onConfirm={leave}
          onClose={() => setConfirmLeave(false)}
        />
      )}
      <Navbar />
    </div>
  );
}
